import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo });

    // Always log to console in all environments
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.props.fallback) {
      return this.props.fallback;
    }

    const isDev = import.meta.env.DEV;

    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-light p-6">
        <div className="w-full max-w-lg text-center">
          <div className="mb-6">
            <div className="mx-auto w-16 h-16 rounded-full bg-status-rejected-bg flex items-center justify-center mb-4">
              <AlertTriangle className="h-8 w-8 text-status-rejected" />
            </div>
            <h1 className="text-xl font-bold text-content-primary mb-2">Something went wrong</h1>
            <p className="text-base text-content-secondary">
              An unexpected error occurred. Please try refreshing the page.
            </p>
          </div>

          <div className="flex justify-center gap-3 mb-6">
            <button
              type="button"
              onClick={this.handleReset}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white border rounded-lg text-sm font-medium text-content-primary hover:bg-surface-light transition-colors"
            >
              <RefreshCw className="h-4 w-4" />
              Try again
            </button>
            <button
              type="button"
              onClick={() => {
                window.location.href = '/';
              }}
              className="inline-flex items-center px-4 py-2 bg-content-primary text-white rounded-lg text-sm font-medium hover:bg-content-primary/90 transition-colors"
            >
              Go to home
            </button>
          </div>

          {isDev && this.state.error && (
            <div className="text-left bg-white border rounded-xl p-4 overflow-auto max-h-80">
              <p className="text-sm font-semibold text-status-rejected mb-2">
                {this.state.error.name}: {this.state.error.message}
              </p>
              {this.state.error.stack && (
                <pre className="text-xs text-content-secondary whitespace-pre-wrap break-words font-mono">
                  {this.state.error.stack}
                </pre>
              )}
              {this.state.errorInfo?.componentStack && (
                <>
                  <p className="text-sm font-semibold text-content-primary mt-4 mb-1">
                    Component Stack:
                  </p>
                  <pre className="text-xs text-content-secondary whitespace-pre-wrap break-words font-mono">
                    {this.state.errorInfo.componentStack}
                  </pre>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }
}
